import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { keys, useBankConnections, useFamily } from "@/api/queries";
import { useAuth } from "@/auth/AuthProvider";
import { useToast } from "@/components/Toast";
import { type BankConnection, BankConnection_Status } from "@/generated/backend/v1/bank_link";
import { formatDate } from "@/plan-logic/lib/dates";
import { reconnectFlinksConnection, refreshFlinksConnection, removeFlinksConnection } from "./api";
import { FlinksDialog, flinksErrorText } from "./FlinksDialog";

/** "4 November 2026" from a protobuf Timestamp. */
function readOn(c: BankConnection): string {
  if (!c.lastRefreshAt) return "";
  const d = new Date(Number(c.lastRefreshAt.seconds) * 1000);
  return formatDate({ year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() });
}

/**
 * Settings: the banks connected through Flinks, whose they are, and whether they're up to date. A bank
 * that needs the user (most Canadian banks ask for a code now and then) is reconnected here: a live
 * read, through Flinks Connect when the bank asks for something. Disconnecting keeps the accounts in
 * the plan with their last balances. Hidden where Flinks isn't configured or nothing is connected.
 */
export function BankConnectionsCard() {
  const { data } = useBankConnections();
  const family = useFamily().data?.family;
  const client = useQueryClient();
  const uid = useAuth().user?.uid ?? "-";
  const toast = useToast();
  const [busy, setBusy] = useState<string>();
  const [confirming, setConfirming] = useState<string>();
  const [error, setError] = useState<string>();
  /** A reconnect that needs the user in Flinks Connect. */
  const [dialog, setDialog] = useState<{ connection: BankConnection; url: string }>();

  const connections = data?.connections ?? [];
  if (!connections.length) return null;

  const owner = (uuid: string) => family?.persons.find((p) => p.uuid === uuid)?.givenName ?? "";
  const reload = () => client.invalidateQueries({ queryKey: keys.bankLink(uid) });
  const done = async (c: BankConnection) => {
    await reload();
    toast(
      c.status === BankConnection_Status.NEEDS_RECONNECT
        ? `${c.institution} didn't send anything new. Try again later.`
        : `${c.institution} is up to date. Its balances come in with your next check-in.`,
    );
  };

  const run = async (id: string, action: () => Promise<void>) => {
    setBusy(id);
    setError(undefined);
    try {
      await action();
    } catch (e) {
      setError(flinksErrorText(e));
    } finally {
      setBusy(undefined);
    }
  };

  const reconnect = (c: BankConnection) =>
    run(c.id, async () => {
      const res = await reconnectFlinksConnection(c.id);
      if (res.iframeUrl) setDialog({ connection: c, url: res.iframeUrl });
      else if (res.connection) await done(res.connection);
    });

  const disconnect = (c: BankConnection) =>
    run(c.id, async () => {
      await removeFlinksConnection(c.id);
      setConfirming(undefined);
      await reload();
      toast(`${c.institution} is disconnected.`);
    });

  return (
    <div className="card pad" id="banks" data-testid="settings-banks">
      <h2 style={{ marginBottom: 4 }}>Connected banks</h2>
      <p className="mut" style={{ marginBottom: 14, maxWidth: "62ch" }}>
        We read their balances each day, read-only through Flinks, and bring them into your plan at
        your next check-in.
      </p>
      {error && (
        <p className="wp-form-err" role="alert">
          {error}
        </p>
      )}
      <div className="wp-banks">
        {connections.map((c) => {
          const needsYou = c.status === BankConnection_Status.NEEDS_RECONNECT;
          const who = owner(c.ownerUuid);
          return (
            <div className="wp-bank" key={c.id} data-testid={`bank-${c.id}`}>
              <div className="wp-bank-name">
                <b>{c.institution}</b>
                <span className="mut">
                  {[who, readOn(c) && `last read ${readOn(c)}`].filter(Boolean).join(" · ")}
                </span>
              </div>
              <span className={`wp-pill ${needsYou ? "warn" : "ok"}`}>
                {needsYou ? "Needs you" : "Up to date"}
              </span>
              {confirming === c.id ? (
                <div className="wp-bank-actions">
                  <span className="mut">
                    Disconnect? Its accounts stay in your plan with their last balances.
                  </span>
                  <button
                    type="button"
                    className="wp-btn wp-btn-danger"
                    disabled={busy === c.id}
                    onClick={() => void disconnect(c)}
                    data-testid={`bank-${c.id}-confirm`}
                  >
                    {busy === c.id ? "Disconnecting…" : "Disconnect"}
                  </button>
                  <button
                    type="button"
                    className="wp-btn g"
                    onClick={() => setConfirming(undefined)}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="wp-bank-actions">
                  {needsYou && (
                    <button
                      type="button"
                      className="wp-btn"
                      disabled={!!busy}
                      onClick={() => void reconnect(c)}
                      data-testid={`bank-${c.id}-reconnect`}
                    >
                      {busy === c.id ? "Reconnecting…" : "Reconnect"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="wp-btn g"
                    disabled={!!busy}
                    onClick={() => setConfirming(c.id)}
                    data-testid={`bank-${c.id}-disconnect`}
                  >
                    Disconnect
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {dialog && (
        <FlinksDialog
          title={`Reconnect ${dialog.connection.institution}`}
          start={async () => dialog.url}
          finish={async () => {
            const c = await refreshFlinksConnection(dialog.connection.id);
            setDialog(undefined);
            await done(c);
          }}
          onClose={() => setDialog(undefined)}
        />
      )}
    </div>
  );
}
